import React from "react";
import { cn } from "@/components/utils/utils";
import { useI18n } from '@/hooks/useI18n';
import { MoreHorizontal, Calendar, AlertTriangle, Check, RefreshCw, CreditCard } from "lucide-react";

interface ItineraryStatusBadgeProps {
  status: string;
  className?: string;
  hideDraftQuote?: boolean;
  userRole?: string;
  /** Testo breve appeso al badge, es. "1/4" per l'avanzamento delle rate. */
  detail?: string | null;
}

export const ItineraryStatusBadge: React.FC<ItineraryStatusBadgeProps> = ({
  status,
  className,
  hideDraftQuote = false,
  userRole,
  detail,
}) => {
  const { t } = useI18n();

  const effectiveStatus = status === 'sended' && userRole === 'traveler' ? 'client-sended' : status;

  const getStatusBadgeStyles = () => {
    switch (effectiveStatus) {
      case 'client-sended':
      case 'pending':
        return 'bg-white border border-gray-200 text-gray-800';
      case 'approved':
        return 'bg-green-100 border border-green-300 text-green-800';
      case 'sended':
        return 'bg-yellow-100 border border-yellow-300 text-yellow-800';
      case 'rejected':
      case 'expired':
        return 'bg-red-200 border border-red-400 text-red-800';
      case 'draft':
        return 'bg-gray-100 border border-gray-300 text-gray-800';
      case 'request_changes':
        return 'bg-white border border-gray-200 text-gray-800';
      // Confermato ma con rate ancora aperte: verde come i completati sarebbe una
      // bugia, rosso un allarme che non c'e'. Ambra = "incassato a meta'".
      case 'installments_in_progress':
        return 'bg-amber-100 border border-amber-300 text-amber-800';
      case 'completed':
      case 'upcoming':
      case 'past':
      case 'ongoing':
        return 'bg-green-600 border border-green-600 text-white';
      default:
        return 'bg-gray-900 text-white border border-gray-700';
    }
  };

  const getStatusText = () => {
    switch (effectiveStatus) {
      case 'pending':
        return t('itineraryStatus.pending');
      case 'approved':
        return t('itineraryStatus.approved');
      case 'sended':
        return t('itineraryStatus.sended');
      case 'client-sended':
        return t('itineraryStatus.client-sended');
      case 'rejected':
        return t('itineraryStatus.rejected');
      case 'expired':
        return t('itineraryStatus.expired');
      case 'draft':
        return t('itineraryStatus.draft');
      case 'request_changes':
        return t('itineraryStatus.requestChanges');
      // Ripiego per gli itinerari confermati senza date: detailedStatus non riesce a
      // dire se il viaggio e' passato, in corso o futuro, e senza questo case il badge
      // resterebbe senza testo.
      case 'completed':
        return t('itineraryStatus.completed');
      case 'upcoming':
        return t('itineraryStatus.completed-upcoming');
      case 'past':
        return t('itineraryStatus.completed-past');
      case 'ongoing':
        return t('itineraryStatus.completed-ongoing');
      case 'installments_in_progress':
        return t('itineraryStatus.installmentsInProgress');
      default:
        return '';
    }
  };

  const getStatusIcon = (): React.ReactNode => {
    switch (effectiveStatus) {
      case 'pending':
      case 'sended':
      case 'ongoing':
      case 'client-sended':

        return <MoreHorizontal className="w-3 h-3" />;
      case 'upcoming':
        return <Calendar className="w-3 h-3" />;
      case 'rejected':
      case 'expired':
        return <AlertTriangle className="w-3 h-3" />;
      case 'past':
      case 'approved':
      case 'completed':
        return <Check className="w-3 h-3" />;
      case 'request_changes':
        return <RefreshCw className="w-3 h-3" />;
      case 'installments_in_progress':
        return <CreditCard className="w-3 h-3" />;
      default:
        return null;
    }
  };

  // Nascondi il badge se hideDraftQuote è true e lo status è draft o quote
  if (hideDraftQuote && (effectiveStatus === 'draft' || effectiveStatus === 'completed')) {
    return null;
  }

  return (
    <div className={cn(
      // w-fit: largo quanto il testo, anche quando il contenitore e' un blocco (cella di tabella).
      // Il testo non va mai a capo: un badge su due righe non si legge come un'etichetta.
      "w-fit shrink-0 whitespace-nowrap px-3 py-1 rounded-full text-xs shadow-sm flex items-center gap-2",
      getStatusBadgeStyles(),
      className
    )}>
      {getStatusIcon()}
      <span className="text-xs font-medium">
        {getStatusText()}
      </span>
      {detail && (
        <span className="text-xs font-semibold tabular-nums opacity-90">
          {detail}
        </span>
      )}
    </div>
  );
};
